import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import {
  FilterType,
  OrderFilterTab,
} from '@services/MemberPreferenceConfigService'
import {
  doGetMemberPreferenceConfig,
  doUpsertMemberPreferenceConfig,
} from '@state/reducers/memberPreferenceConfigReducer'
import { setOrderFilters } from '@state/reducers/orderReducer'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { OrderStatusEnum } from '../../../common/interfaces'
import { WorklistQueryParams } from '@/utils/urlQueries'
import { X } from 'lucide-react'

const DEFAULT_TABS: OrderFilterTab[] = Object.values(OrderStatusEnum).map(
  (s) => ({
    id: s,
    type: FilterType.STATUS,
    value: s,
  }),
)

type Props = {
  memberId: number
  updateParam: (key: keyof WorklistQueryParams, value: any) => void
  activeValue: string | null
}

export const OrderFilterTabs: React.FC<Props> = ({
  memberId,
  updateParam,
  activeValue,
}) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const config = useAppSelector(
    ({ memberPreferenceConfig }) => memberPreferenceConfig.config,
  )

  const savedTabs: OrderFilterTab[] = config?.orderFilters?.length
    ? (config.orderFilters as OrderFilterTab[])
    : DEFAULT_TABS

  const [isEditMode, setIsEditMode] = useState(false)
  const [draftTabs, setDraftTabs] = useState<OrderFilterTab[]>(savedTabs)

  // Popover state for adding new filter
  const [addAnchorEl, setAddAnchorEl] = useState<HTMLElement | null>(null)
  const [newFilterType, setNewFilterType] = useState<FilterType>(
    FilterType.STATUS,
  )
  const [newFilterValue, setNewFilterValue] = useState<string>('')

  useEffect(() => {
    dispatch(doGetMemberPreferenceConfig(memberId))
  }, [memberId])

  useEffect(() => {
    setDraftTabs(savedTabs)
  }, [config?.orderFilters])

  const handleTabChange = (_: React.SyntheticEvent, value: string | null) => {
    if (!value) return
    const tab = savedTabs.find((t) => t.id === value)
    if (!tab) return

    if (tab.type === FilterType.STATUS) {
      dispatch(
        setOrderFilters({
          status: tab.value as OrderStatusEnum,
          hasRadiologyStudy: undefined,
        }),
      )
      updateParam('state', tab.value)
    } else if (tab.type === FilterType.RADIOLOGY_STUDY) {
      dispatch(setOrderFilters({ hasRadiologyStudy: true, status: undefined }))
      updateParam('state', tab.id)
    }
  }

  const handleEnterEdit = () => {
    setDraftTabs(savedTabs)
    setIsEditMode(true)
  }

  const handleCancelEdit = () => {
    setDraftTabs(savedTabs)
    setIsEditMode(false)
  }

  const handleResetTabs = () => {
    setDraftTabs(DEFAULT_TABS)
  }

  const handleRemoveTab = (id: string) => {
    setDraftTabs((prev) => prev.filter((t) => t.id !== id))
  }

  const handleConfirmEdit = () => {
    dispatch(
      doUpsertMemberPreferenceConfig({
        ...(config ?? {}),
        memberId,
        reportDictationMode: config?.reportDictationMode ?? 'RT_ASR',
        orderFilters: draftTabs,
      }),
    )
    setIsEditMode(false)
  }

  const openAddPopover = (e: React.MouseEvent<HTMLButtonElement>) => {
    setNewFilterType(FilterType.STATUS)
    setNewFilterValue('')
    setAddAnchorEl(e.currentTarget)
  }

  const closeAddPopover = () => setAddAnchorEl(null)

  const handleAddFilter = () => {
    if (newFilterType === FilterType.RADIOLOGY_STUDY) {
      const alreadyExists = draftTabs.some(
        (t) => t.type === FilterType.RADIOLOGY_STUDY,
      )
      if (!alreadyExists) {
        setDraftTabs((prev) => [
          ...prev,
          {
            id: FilterType.RADIOLOGY_STUDY,
            type: FilterType.RADIOLOGY_STUDY,
            value: true,
          },
        ])
      }
      closeAddPopover()
      return
    }
    if (!newFilterValue) return
    const alreadyExists = draftTabs.some(
      (t) => t.type === 'status' && t.value === newFilterValue,
    )
    if (!alreadyExists) {
      setDraftTabs((prev) => [
        ...prev,
        {
          id: newFilterValue,
          type: FilterType.STATUS,
          value: newFilterValue,
        },
      ])
    }
    closeAddPopover()
  }

  const usedStatusValues = draftTabs
    .filter((t) => t.type === FilterType.STATUS)
    .map((t) => t.value as string)

  const availableStatusValues = Object.values(OrderStatusEnum).filter(
    (s) => !usedStatusValues.includes(s),
  )

  const getTabLabel = (tab: OrderFilterTab): string => {
    if (tab.label) {
      return tab.label
    }
    if (tab.type === 'radiologyStudy') {
      return intl.formatMessage({
        id: 'filter.radiologyStudy',
        defaultMessage: 'Images reçues',
      })
    }
    return intl.formatMessage({
      id: `orderStatus.${tab.value}`,
      defaultMessage: String(tab.value).replace(/_/g, ' '),
    })
  }

  const displayTabs = isEditMode ? draftTabs : savedTabs

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isEditMode ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
          }}
        >
          {draftTabs.map((tab) => (
            <Box
              key={tab.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px dashed #3A0CA1',
                borderRadius: '4px',
                px: 1,
                py: 0.5,
                backgroundColor: '#fff',
                fontSize: '0.86rem',
              }}
            >
              {getTabLabel(tab)}
              <IconButton
                size="small"
                onClick={() => handleRemoveTab(tab.id)}
                sx={{ ml: 0.5, p: 0.1 }}
                color="error"
              >
                <X size={14} />
              </IconButton>
            </Box>
          ))}

          <Tooltip title="Ajouter un filtre">
            <IconButton size="small" onClick={openAddPopover}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Réinitialiser">
            <IconButton size="small" onClick={handleResetTabs}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Confirmer">
            <IconButton
              size="small"
              color="success"
              onClick={handleConfirmEdit}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Annuler">
            <IconButton size="small" color="error" onClick={handleCancelEdit}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <ToggleButtonGroup
          value={activeValue}
          exclusive
          color="secondary"
          onChange={handleTabChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              backgroundColor: '#fff',
              '&.Mui-selected': {
                backgroundColor: '#3A0CA1',
                color: '#fff',
                '&:hover': { backgroundColor: '#3A0CA1' },
              },
            },
          }}
        >
          {displayTabs.map((tab) => (
            <ToggleButton key={tab.id} value={tab.id}>
              {getTabLabel(tab)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {!isEditMode && (
        <Tooltip title="Personnaliser les filtres">
          <IconButton size="small" onClick={handleEnterEdit}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Popover
        open={Boolean(addAnchorEl)}
        anchorEl={addAnchorEl}
        onClose={closeAddPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 260,
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel>
              {intl.formatMessage({
                id: 'filter.type',
                defaultMessage: 'Type',
              })}
            </InputLabel>
            <Select
              label={intl.formatMessage({
                id: 'filter.type',
                defaultMessage: 'Type',
              })}
              value={newFilterType}
              onChange={(e: SelectChangeEvent) => {
                setNewFilterType(e.target.value as FilterType)
                setNewFilterValue('')
              }}
            >
              <MenuItem value="status">Statut</MenuItem>
              <MenuItem value="radiologyStudy">Images reçues</MenuItem>
            </Select>
          </FormControl>

          {newFilterType === FilterType.STATUS && (
            <FormControl size="small" fullWidth>
              <InputLabel>Valeur</InputLabel>
              <Select
                label={intl.formatMessage({
                  id: 'filter.value',
                  defaultMessage: 'Valeur',
                })}
                value={newFilterValue}
                onChange={(e: SelectChangeEvent) =>
                  setNewFilterValue(e.target.value)
                }
                disabled={availableStatusValues.length === 0}
              >
                {availableStatusValues.map((s) => (
                  <MenuItem key={s} value={s}>
                    {intl.formatMessage({
                      id: `orderStatus.${s}`,
                    })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Button
            variant="contained"
            size="small"
            onClick={handleAddFilter}
            disabled={newFilterType === FilterType.STATUS && !newFilterValue}
          >
            Ajouter
          </Button>
        </Box>
      </Popover>
    </Box>
  )
}
